'use client'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Center,
    Checkbox,
    CircularProgress,
    Collapse,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    Heading,
    HStack,
    Input,
    Select,
    Show,
    Step,
    StepDescription,
    StepIcon,
    StepIndicator,
    StepNumber,
    Stepper,
    StepSeparator,
    StepStatus,
    StepTitle,
    Text,
    useColorMode,
    useSteps,
    VStack
} from "@chakra-ui/react";
import {Library} from "@googlemaps/js-api-loader";
import {useEffect, useMemo, useRef, useState} from "react";
import {useJsApiLoader} from "@react-google-maps/api";
import {
    BarDeal,
    ChainDeal,
    NewBarDealSuggestion,
    NewChainDealSuggestion,
    WriteBarDealSuggestion,
    WriteChainDealSuggestion
} from "../../../../interfaces/IDeal";
import {THEME_ID, ThemeProvider} from "@mui/material/styles";
import {useLocale, useTranslations} from "next-intl";
import moment from "moment/moment";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import DealView from "../../../../components/ui/DealView";
import {db} from "../../../../firebase/config";
import {doc, query, runTransaction, where} from "@firebase/firestore";
import {
    Bar,
    BarChain,
    ChainSubmission,
    MakeBarDealSuggestion,
    MakeChainDealSuggestion,
    NewBarSubmission,
    RawNewBarSubmission,
    RemoveDeals
} from "../../../../interfaces/Bar";
import {collection, getDocs} from "firebase/firestore";
import {Link} from "../../../../i18n/routing";
import ThanksForSubmission from "../../../../components/ui/submissions/ThanksForSubmission";
import {areBarsEqual, areDealListsEqual} from "../../../../components/tools/compareBars";
import {getBarWithDeals, getChainDeals, getChains} from "../../../../components/tools/getBarWithDeals";
import {BeatLoader} from "react-spinners";
import {addRegionToLocale} from "../../../../components/tools/momentLocale";
import "moment/locale/lv"
import "moment/locale/en-gb"
import {approveInTransaction, NewBarChanges,} from "../../../../components/tools/submissions";
import {
    HeaderWithBackground,
    PageContentCard,
    PageContentHeading
} from "../../../../components/ui/HeaderWithBackground";
import {useIsModerator} from "../../../../components/tools/useUser";
import {SearchIcon} from "@chakra-ui/icons";
import {sendMessage} from "../../../../components/serverActions/sendMessage";
import Editor from "../../../../components/ui/submissions/Editor";
import {muiTheme} from "../../../../components/tools/muiTheme";
import PlaceResult = google.maps.places.PlaceResult;

const libs = ["places"] as Library[]

export default function AddBar(
    {params}: { params: { barId: string[] } }
) {
    const locale = useLocale()
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.NEXT_PUBLIC_MAPS_APIKEY ?? "",
        language: locale,
        libraries: libs
    })

    const [mapSearchResult, setMapSearchResult] = useState<PlaceResult>({});
    const inputRef = useRef<HTMLInputElement|null>(null);

    moment.locale(addRegionToLocale(locale))

    const t = useTranslations("addbar")

    const steps = [
        { title: t("steps_place"), description: t("steps_place_description") },
        { title: t("steps_deals"), description: t("steps_deals_description") },
        { title: t("steps_finish"), description: t("steps_finish_description") },
    ]

    const { activeStep, setActiveStep } = useSteps({
        index: 0,
        count: steps.length,

    }, )

    useEffect(() => {
        if (!isLoaded || loadError || activeStep != 0) return;

        const options = {
            componentRestrictions: { country: "lv" },
            fields: ["name", "formatted_address", "place_id", "geometry"],
            types: ["establishment"]
        };

        const handlePlaceChanged = async(address) => {
            if (!isLoaded) return;
            const place = address.getPlace()
            console.log("Place", place)

            if (!place || !place.name) {
                setMapSearchResult({});
                return;
            }
            setMapSearchResult(place)
        };


        if (inputRef.current != null) {
            const autocomplete = new google.maps.places.Autocomplete(inputRef.current, options);
            autocomplete.addListener("place_changed", () => handlePlaceChanged(autocomplete));
        }

        // return () => autocomplete.removeListener("place_changed", handlePlaceChanged);
    }, [isLoaded, loadError, activeStep]);

    const [deals, setDeals] = useState<NewBarDealSuggestion[]>([])
    const [chainDeals, setChainDeals] = useState<NewChainDealSuggestion[]>([])
    const [initialDeals, setInitialDeals] = useState<BarDeal[]>([])
    const [initialChainDeals, setInitialChainDeals] = useState<ChainDeal[]>([])
    const [bar, setBar] = useState<NewBarSubmission|undefined>()
    const [chains, setChains] = useState<BarChain[]|undefined>(undefined)
    const [barFromMaps, setBarFromMaps] = useState<NewBarSubmission|undefined>(undefined)
    const [originalBar, setOriginalBar] = useState<Bar|undefined|null>(undefined)

    const { colorMode } = useColorMode();

    const [saving, setSaving] = useState<NewBarChanges | undefined>()
    const [approving, setApproving] = useState<NewBarChanges | undefined>()
    const [saved, setSaved] = useState<NewBarChanges | undefined>()

    const chain = useMemo(() => chains?.find(c => c.id == bar?.chain?.original?.id), [bar?.chain?.original?.id, chains])

    useEffect( () => {
        setSaved(undefined)
        const saveData = async () => {
            await runTransaction(db, async (tx) => {
                const barDoc = doc(collection(db, "submissions_bars"))
                const chainDoc = doc(collection(db, "submissions_chains"))
                if (saving?.bar?.chain) tx.set(chainDoc, ChainSubmission(saving.bar.chain.id, saving.bar?.chain))
                tx.set(barDoc, {
                    name: saving?.bar.name,
                    chain: saving?.bar?.chain ? chainDoc : null,
                    address: saving?.bar.address,
                    maps_id: saving?.bar.maps_id,
                    description: saving?.bar.description ?? null,
                    location: saving?.bar?.location ?? null,
                    isClosed: closed
                } as RawNewBarSubmission)
                saving?.deals.forEach((deal) => {
                    tx.set(doc(collection(db, "submissions_deals")), WriteBarDealSuggestion(barDoc, deal))
                })
                saving?.chainDeals.forEach(chainDeal => {
                    tx.set(doc(collection(db, "submissions_deals")), WriteChainDealSuggestion(chainDoc, chainDeal))
                })
            })
        }
        if (saving) saveData().catch(reason => {
            setSaveError(reason.message)
            setSaved(saving)
        }).then(value => {
            sendMessage(bar?.name ?? "?")
        })
        setSaving(undefined)
    }, [chain, saving]);

    /*useEffect(() => {
        const load = async () => {
            if (!barFromMaps?.name) return undefined
            await delay(200)
            if (barFromMaps) {
                const bar = await getBar(barFromMaps.maps_id)
                if (bar) {
                    setBar(ExistingBarSubmission(bar))
                    setOriginalBar(bar)
                } else {
                    setBar(barFromMaps)
                    setOriginalBar(null)
                }
                setSearchAgain(false)
            }
        }
        load()
    }, [barFromMaps]);*/

    useEffect(() => {
        const load = async () => {
            if (bar?.chain?.original?.id) {
                const deals = await getChainDeals(bar.chain.original.id)
                setChainDeals(deals.map(a => MakeChainDealSuggestion(a)))
                setInitialChainDeals(deals)
            }
        }
        load()
    }, [bar?.chain]);

    const [closed, setClosed] = useState(false)

    const [inputBarId, setInputBarId] = useState<string | undefined>(undefined)
    useEffect(() => {
        // Parse path params.
        // If just 1, it's bar id
        // If /c/<id>, it's chain id. So we find first bar in that chain and use that.
        const getFirstBarFromChain = async (chainId: string) => {
            const getBars = query(
                collection(db, "bars"),
                where("chain", "==", doc(db, "chains", chainId))
            )
            const barId = (await getDocs(getBars)).docs[0].id
            setInputBarId(barId)
        }
        if (!params?.barId) {
            setInputBarId(undefined)
        } else if (params?.barId.length == 1) {
            setInputBarId(params.barId[0])
        } else if (params?.barId.length == 2 && params?.barId[0] == "c") {
            getFirstBarFromChain(params.barId[1])
        }
    }, [params?.barId]);

    const [loadingInputBar, setLoadingInputBar] = useState(inputBarId !== undefined)

    useEffect(() => {
        // Initialize state - load bar by id and set original* fields.
        const get = async () => {
            if (barFromMaps?.maps_id || inputBarId) {
                const barData = await getBarWithDeals(barFromMaps?.maps_id ? barFromMaps.maps_id : inputBarId ?? "")
                if (barData) {
                    // This bar already exists in our DB.
                    const bar = {
                        maps_id: barData.id,
                        name: barData.name,
                        description: barData.description,
                        location: barFromMaps?.location ?? barData.location,
                        address: barData.address,
                        chain: barData.chain ? ChainSubmission(barData.chain.id, barData.chain) : undefined
                    } as NewBarSubmission
                    setDeals(barData.deals.map(a => MakeBarDealSuggestion(a)))
                    setInitialDeals(barData.deals)

                    setBar(bar)
                    setOriginalBar(RemoveDeals(barData))
                    if (inputBarId) setActiveStep(1)
                } else {
                    // New bar
                    setBar(barFromMaps)
                    setOriginalBar(null)
                }
                setLoadingInputBar(false)
            }
        }
        get()
    }, [inputBarId, barFromMaps, setActiveStep]);

    /*useEffect(() => {
        const load = async () => {
            if (barData?.chain?.id) {
                const deals = await getChainDeals(barData.chain.id)
                console.log("Chain deals", deals)
                setChainDeals(deals.map(a => MakeChainDealSuggestion(a)))
                setInitialChainDeals(deals)
            }
        };
        load();

    }, [barData?.chain?.id]);
*/
    useEffect(() => {
        setBarFromMaps(NewBarSubmission(mapSearchResult))
    }, [mapSearchResult]);

    function nextPressed() {
        if (activeStep < 2) setActiveStep(activeStep + 1)
        else if (bar) {
            const data = {
                    bar: {
                        chain: bar.chain,
                        address: bar.address,
                        description: bar.description,
                        name: bar.name,
                        maps_id: bar.maps_id,
                        location: bar.location
                    },
                    deals: deals,
                    chainDeals: chainDeals,
                    originalBar: originalBar != null ? originalBar : undefined,
                    originalDeals: initialDeals,
                    originalChainDeals: initialChainDeals
                } as NewBarChanges
            if (approve) {
                setApproving(data)
            } else {
                console.log("Saving ", data)
                setSaving(data)
            }
        }
    }

    const [saveError, setSaveError] = useState<string|undefined>()

    useEffect(() => {
        const approve = async () => {
            if (approving) {
                try {
                    await runTransaction(db, async (tx) => {
                        // @ts-ignore
                        await approveInTransaction(tx, approving)
                    })
                } catch (e) {
                    setSaveError(e.message)
                } finally {
                    setSaved(approving)
                    setSaving(undefined)
                }

            }
        }
        approve()
    }, [approving])

    const [editorActive, setEditorActive] = useState(false)


    const barsEqual = useMemo(() => {
        return !bar || (originalBar && areBarsEqual(originalBar, bar)
            && areDealListsEqual(initialDeals, deals)
            && areDealListsEqual(initialChainDeals, chainDeals)
        )
    }, [bar, chainDeals, deals, originalBar, initialChainDeals, initialDeals])

    const canProceed = useMemo(() => {
        //!bar || ((existingBarId?.length ?? 1) > 0 && activeStep == 0) || saving != null
        if (activeStep == 0) {
            return bar
        } else if (activeStep == 1) {
            return !barsEqual
        } else if (activeStep == 2) {
            return saving == null
        } else {
            return false
        }
    }, [activeStep, bar, barsEqual, saving])

    const isModerator = useIsModerator()

    const [approve, setApprove] = useState(false)

    useEffect(() => {
        const a = async () => {
            setChains(await getChains())
        }
        a()
    }, []);


    function updateChainDescription(description: string) {
        if (bar?.chain) {
            setBar({
                ...bar,
                chain: {
                    ...bar.chain,
                    description
                }
            })
        }
    }

    function updateBarDescription(description: string) {
        if (bar) {
            setBar({
                ...bar,
                description: description
            })
        }
    }

    const mod = useIsModerator()

    const [searchAgain, setSearchAgain] = useState(false)

    return (
        <>
            <title>{inputBarId ? t("title_edit") : t("title_add")}</title>
            <meta name="robots" content="noindex"></meta>
            <HeaderWithBackground fullHeight={true} title={inputBarId ? t("title_edit") : t("title_add")} bg="/images/taps.webp">
                <PageContentCard header={
                    <VStack>
                        <PageContentHeading title={inputBarId ? t("title_edit") : t("title_add")}/>
                        <Badge colorScheme="green" marginBottom="10px" fontSize="lg" marginTop="10px">{bar?.name}</Badge>
                        <Stepper index={activeStep} flexWrap="wrap">

                            {steps.map((step, index) => (
                                <Step key={index} onClick={e => {
                                    if (mod && index == 0) setActiveStep(index)}
                                }>
                                    <StepIndicator>
                                        <StepStatus
                                            complete={<StepIcon />}
                                            incomplete={<StepNumber />}
                                            active={<StepNumber />}
                                        />
                                    </StepIndicator>
                                    <Box flexShrink='1'>
                                        <StepTitle>{step.title}</StepTitle>
                                        <Show above="md"><StepDescription>{step.description}</StepDescription></Show>
                                    </Box>
                                    <StepSeparator/>
                                </Step>)
                            )}
                        </Stepper>
                    </VStack>
                }>
                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={addRegionToLocale(locale)}>
                        <ThemeProvider theme={{[THEME_ID]: muiTheme(colorMode)}}>
                            <VStack>
                                { !loadingInputBar ? (
                                    <Box w="100%">
                                        { activeStep == 0 ? (
                                            <Box>
                                                <VStack align="start" justify="stretch">
                                                    <FormControl isRequired hidden={bar !== undefined && !searchAgain}>
                                                        <FormLabel>{t("search_map")}</FormLabel>
                                                        <Input type="text" ref={inputRef}></Input>
                                                    </FormControl>
                                                    <HStack justify="start">

                                                        {
                                                            bar !== undefined && !searchAgain && (
                                                                <Button onClick={() => {
                                                                    setSearchAgain(true)
                                                                    // @ts-ignore
                                                                    inputRef.current.value = ""
                                                                }} leftIcon={<SearchIcon/>}>{t("search_map")}</Button>
                                                            )
                                                        }
                                                        {
                                                            searchAgain && (
                                                                <Button onClick={() => setSearchAgain(false)}>{t("cancel")}</Button>
                                                            )
                                                        }
                                                    </HStack>
                                                    <FormControl isRequired>
                                                        <FormLabel>{t("bar_name")}</FormLabel>
                                                        <Input type="text" value={bar?.name ?? ""} onChange={ e => {
                                                            setBar(bar ? {
                                                                ...bar,
                                                                name: e.target.value
                                                            } : undefined)
                                                        }}></Input>
                                                    </FormControl>
                                                    <FormControl isRequired>
                                                        <FormLabel>{t("bar_address")}</FormLabel>
                                                        <Input type="text" disabled value={bar?.address ?? ""}></Input>
                                                    </FormControl>

                                                    {
                                                        bar?.address && isModerator && (
                                                            <FormControl>
                                                                <FormLabel>{t("chain")}</FormLabel>
                                                                <Select isDisabled={chains === undefined} value={bar?.chain?.id} onChange={event => {
                                                                    const chain = chains?.find(c => c.id == event.target.value)
                                                                    if (chain) {
                                                                        setBar({
                                                                            ...bar,
                                                                            chain: ChainSubmission(event.target.value, chain)
                                                                        })
                                                                    }
                                                                }}>
                                                                    <option key={undefined} value={undefined}>No chain</option>
                                                                    { chains !== undefined && chains.map(value => (
                                                                        <option key={value.id} value={value.id}>{value.name}</option>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        )
                                                    }
                                                </VStack>
                                            </Box>) : <></>}
                                        { activeStep == 1 && bar && (
                                            <Editor initialDeals={initialDeals}
                                                    deals={deals}
                                                    dealsChanged={setDeals}
                                                    chain={bar?.chain}
                                                    bar={bar}
                                                    initialChainDeals={initialChainDeals}
                                                    chainDeals={chainDeals}
                                                    chainDealsChanged={setChainDeals}
                                                    onActivated={setEditorActive}
                                                    chainDescriptionChanged={updateChainDescription}
                                                    barDescriptionChanged={updateBarDescription}
                                                    markClosed={() => {
                                                        setClosed(true)
                                                        nextPressed()
                                                    }}
                                            />
                                        ) }
                                        {
                                            activeStep == 2 && !saving && !approving && (
                                                <Grid gap={4} templateColumns='repeat(2, 1fr)'>
                                                    <GridItem colSpan={2}>
                                                        <Card variant="filled" bg="green">
                                                            <CardHeader>
                                                                <Heading size="md">{t("steps_finish")}</Heading>
                                                            </CardHeader>
                                                            <CardBody>
                                                                {t("steps_finish_text")}
                                                            </CardBody>
                                                        </Card>
                                                    </GridItem>
                                                    <GridItem colSpan={1}>
                                                        <Card variant="outline">
                                                            <CardHeader><Heading size="md">{t("bar_name")}</Heading></CardHeader>
                                                            <CardBody>
                                                                <Text>{bar?.name}</Text>
                                                            </CardBody>
                                                        </Card>
                                                    </GridItem>
                                                    <GridItem colSpan={1}>
                                                        <Card variant="outline">
                                                            <CardHeader><Heading size="md">{t("bar_address")}</Heading></CardHeader>
                                                            <CardBody>
                                                                <Text>{bar?.address}</Text>
                                                            </CardBody>
                                                        </Card>
                                                    </GridItem>
                                                    <GridItem colSpan={2}>
                                                        <Card variant="outline">
                                                            {closed && (
                                                                <CardHeader>
                                                                    <Heading size="md">Bar will be marked as closed</Heading>
                                                                </CardHeader>
                                                            )}
                                                            {!closed && (
                                                                <>
                                                                    <CardHeader><Heading size="md">{t("deals")}</Heading></CardHeader>
                                                                    <CardBody>
                                                                        {deals.map(deal => <DealView deal={deal} key={deal.description}/>)}
                                                                    </CardBody>
                                                                </>
                                                            )}
                                                            <CardFooter>
                                                                <Button variant="outline" onClick={() => {
                                                                    setClosed(false);
                                                                    setActiveStep(1)
                                                                }}>{t("edit")}</Button>
                                                            </CardFooter>
                                                        </Card>
                                                    </GridItem>
                                                </Grid>
                                            )
                                        }
                                        {(saving || approving) && !saved && (
                                            <Center>
                                                <CircularProgress isIndeterminate={true}></CircularProgress>
                                            </Center>
                                        )}
                                        {saved != null && !saveError && (
                                            <Center>
                                                <ThanksForSubmission barName={approving?.bar?.name ?? saving?.bar.name ?? ""}/>
                                            </Center>
                                        )}
                                    </Box>
                                ) : <Center><CircularProgress isIndeterminate={true}/> </Center>}
                                <Box>
                                    <VStack alignItems="start">
                                        {
                                            activeStep == 1 && barsEqual && !editorActive && (
                                                <Alert status='warning'>
                                                    <AlertIcon />
                                                    {t("no_changes")}
                                                    <Link href={{pathname: "/"}}>
                                                        <Button size="sm" variant="outline" marginLeft="10px">{t("no_changes_exit")}</Button>
                                                    </Link>
                                                </Alert>
                                            )
                                        }
                                        {
                                            isModerator && activeStep == 2 && !saving && !saved && (
                                                <Checkbox isChecked={approve} onChange={e => setApprove(e.target.checked)}>Publish immediately</Checkbox>
                                            )
                                        }
                                        <Collapse in={saveError !== undefined && isModerator}>
                                            <Alert status="error">
                                                <AlertIcon/>
                                                <AlertTitle>Error saving</AlertTitle>
                                                <AlertDescription>{saveError}</AlertDescription>
                                            </Alert>
                                        </Collapse>
                                        <Collapse in={!editorActive}>
                                            <Button
                                                isLoading={originalBar === undefined && (bar?.name?.length ?? 0) > 0}
                                                variant="primary"
                                                isDisabled={ !canProceed }
                                                onClick={nextPressed}
                                                spinner={<BeatLoader size={9}/>}>
                                                {
                                                    (activeStep < 2 ? t("next") : (approve ? "Publish" : t("send")))
                                                }
                                            </Button>
                                        </Collapse>
                                    </VStack>
                                </Box>
                            </VStack>

                        </ThemeProvider>
                    </LocalizationProvider>
                </PageContentCard>
            </HeaderWithBackground>
        </>
    )
}